<template>
    <div class="flex flex-wrap items-center mt-2 xs:mr-1 mb-1 xs:px-2">
        <span class="text-uppercase font-bold mr-2 mb-2 items-center text-sm">Templates:</span>
        <div class="inline-block mb-2 btn btn-sm btn-shadow-none text-sm xs:w-48 sm:min-w-xxs" style="text-align:left;" @click="openModal">
            {{ truncate(template.name, 24, '...') || 'None' }}
            <span v-if="templateIsDefault(template)" class="text-sm ml-px pl-px italic text-gray-500">Default</span>
            <i class="fas fa-caret-down ml-1 float-right"></i>
        </div>

        <modal id="modalTemplates" @close="showModal=false" ref="modalRef" v-if="showModal">
            <template #header>
                Chart Templates
            </template>
            <template #body>
                <div class="templates-wrapper">
                    <div class="flex flex-wrap">
                        <div class="w-full p-0 mb-3">
                            <ul class="list-none p-0">
                                <li v-if="displaySaveLink">
                                    <a href="#" @click.prevent="saveNewTemplate">
                                        <i class="fas fa-save mr-2 text-gray-400"></i>
                                        Save Settings As Chart Template
                                    </a>
                                </li>
                                <li>
                                    <a :href="getHrefLink('my-folder.chart-templates.index')"><i class="fas fa-tasks mr-2 text-gray-400"></i>Manage Chart Templates</a>
                                </li>
                            </ul>
                        </div>
                        <hr class="w-full h-px mb-4 bg-smoke-100 border-t-1-px" />
                        <div class="w-full p-0 mb-3">
                            <p class="font-bold mb-1">Select a Template to Apply:</p>
                            <div class="template-list">
                                <ul class="list-none p-0">
                                    <li @click="applyTemplate('0')">
                                        <i class="far fa-check-square mr-1 text-gray-400"></i>
                                        None
                                    </li>
                                    <li @click="applyTemplate(item)" v-for="item in templates">
                                        <i class="far fa-check-square mr-1 text-gray-400" :class="{'text-yellow-500' : template.id == item.id}"></i>
                                        {{ item.name }}
                                        <span v-if="templateIsDefault(item)" class="text-sm ml-px pl-px italic text-gray-500">Default</span>
                                    </li>
                                    <li class="text-gray-500 pt-2 border-b-2 pointer-events-none">Public Templates</li>
                                    <li @click="applyTemplate(item)" v-for="item in publicTemplates">
                                        <i class="far fa-check-square mr-1 text-gray-400" :class="{'text-yellow-500' : template.id == item.id}"></i>
                                        <span class="text-sm mr-px pr-px italic text-gray-500">*</span>
                                        {{ item.name }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div v-if="userIsJim()">
                        Make public
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="close">Close</div>
                </div>
            </template>
        </modal>

        <modal id="modalTemplateSave" @close="showTemplateSaveModal=false" v-if="showTemplateSaveModal">
            <template #header>
                Save Settings As Chart Template
            </template>
            <template #body>
                <div class="templates-wrapper">
                    <div class="w-full p-0 mb-3 error" v-if="formError">
                        {{ formError }}
                    </div>
                    <label for="template-name" class="w-full sm:w-auto">Template Name:</label>
                    <div class="form-group-addon form-group-addon-sm">
                        <span class="relative w-full">
                            <input class="form-control form-control-sm input-addon-field select-template-dropdown" autocomplete="off" @input="checkTemplateName" type="text" name="name" v-model="form.name" placeholder="Enter or select a template name....">
                            <i @click="resetTemplateList" class="fas fa-times show-templates-icon text-gray-500"></i>
                        </span>
                        <span class="input-addon-item input-addon-item-ghost btn-white cursor-pointer" @click="showTemplateList = !showTemplateList">
                            <i class="fas fa-angle-double-up" v-if="showTemplateList"></i>
                            <i class="fas fa-list-ul angle-double-down" v-else></i>
                        </span>
                    </div>
                    <div class="template-list" v-if="showTemplateList">
                        <ul class="list-none p-0">
                            <li @click="selectTemplateFromList(item)" v-for="item in templates">
                                {{ item.name }}
                            </li>
                        </ul>
                    </div>
                    <div class="form-group mt-2" v-if="!selectedTemplate && !showTemplateList">
                        <label for="template-description" class="w-full sm:w-auto">Template Description:</label>
                        <input class="form-control form-control-sm mt-1" autocomplete="off" @input="selectedTemplate=false" type="text" name="description" v-model="form.description" placeholder="Enter a template description....">
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="showTemplateSaveModal = false">Cancel</div>
                    <div class="btn btn-sm btn-yellow inline-flex mx-1" @click="saveTemplate">Save Template</div>
                </div>
            </template>
        </modal>

        <modal @close="showReplaceTemplateModal=false" v-if="showReplaceTemplateModal">
            <template #header>
                <div class="text-yellow-500">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    Are you sure you want to save?
                </div>
            </template>
            <template #body>
                <div class="py-4">
                    A template with this name already exists. Do you want to replace it?
                </div>
            </template>
            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="showReplaceTemplateModal = false">Cancel</div>
                    <div class="btn btn-sm btn-yellow inline-flex mx-1" @click="replaceTemplate">Replace</div>
                </div>
            </template>
        </modal>

        <flash-message ref="flashRef" :secondsToDisplay="2"></flash-message>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
    initialTemplates: Array,
    initialPublicTemplates: Array,
    initialDefault: Object,
    defaultTemplate: Object,
    displaySaveLink: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['apply', 'created']);

const templates = ref(props.initialTemplates);
const publicTemplates = ref(props.initialPublicTemplates);
const template = ref(props.initialDefault ? props.initialDefault : 0);
const showModal = ref(false);
const selectedTemplate = ref(false);
const showTemplateSaveModal = ref(false);
const showTemplateList = ref(false);
const showReplaceTemplateModal = ref(false);
const form = ref({
    name: '',
    description: ''
});
const formError = ref(false);
const flashRef = ref(null);

function truncate(value, limit, suffix) {
    if (value && value.length > limit) {
        return value.substring(0, limit) + suffix;
    }
    return value;
}

const checkTemplateName = () => {
    showTemplateList.value = false;
    selectedTemplate.value = templates.value.find(t => t.name === form.value.name);
    if (selectedTemplate.value) {
        form.value.name = selectedTemplate.value.name;
    }
};

const resetTemplateList = () => {
    form.value.name = '';
    form.value.description = '';
    showTemplateList.value = false;
    selectedTemplate.value = false;
    formError.value = false;
};

const getHrefLink = (routeName) => {
    return route(routeName);
};

const openModal = () => {
    showModal.value = true;
    selectedTemplate.value = false;
    formError.value = false;
};

const saveNewTemplate = () => {
    showModal.value = false;
    form.value.name = '';
    form.value.description = '';
    showTemplateSaveModal.value = true;
    showTemplateList.value = false;
};

const selectTemplateFromList = (pTemplate) => {
    selectedTemplate.value = pTemplate;
    form.value.name = pTemplate.name;
    showTemplateList.value = false;
};

const applyTemplate = (pTemplate) => {
    showModal.value = false;
    template.value = pTemplate;
    if (template.value === '0') {
        console.log('Applying default template');
        emit('apply', { default: true });
        return;
    }
    console.log(`Applying template [${template.value.name}]`);
    emit('apply', template.value);
};

const getTemplate = () => {
    return window.MainChart.asTemplate({ main: true, annotations: true });
};

const reset = () => {
    template.value = 0;
};

const replaceTemplate = () => {
    showTemplateSaveModal.value = false;
    selectedTemplate.value = templates.value.find(t => t.name === form.value.name);
    saveTemplate();
};

const saveTemplate = () => {
    if (!form.value.name && !form.value.description && !selectedTemplate.value) {
        formError.value = 'The given data was invalid.';
        return;
    }

    // check if we have such template
    if (templates.value.find(t => t.name === form.value.name) && !showReplaceTemplateModal.value) {
        showReplaceTemplateModal.value = true;
        return;
    }

    if (!selectedTemplate.value) {
        return createTemplate();
    }

    const route = window.route('interactive-charts.templates.update', selectedTemplate.value.id);
    const request = selectedTemplate.value;
    request.template = getTemplate();

    axios.put(route, request)
        .then((response) => {
            showModal.value = false;
            flashRef.value.display('Template has been updated!');
            showTemplateSaveModal.value = false;
            emit('created', response.data);
        }).catch((error) => {
            flashRef.value.error('Error updating template!');
            console.error(error);
        });

    selectedTemplate.value = false;
    showReplaceTemplateModal.value = false;
};

const createTemplate = () => {
    const route = window.route('interactive-charts.templates.store');
    const request = form.value;
    request.template = getTemplate();

    formError.value = false;

    axios.post(route, request)
        .then((response) => {
            templates.value.push(response.data);
            template.value = templates.value[templates.value.length - 1];
            showTemplateSaveModal.value = false;
            flashRef.value.display('Template has been created!');
            emit('created', response.data);
        }).catch(({ response }) => {
            formError.value = response.data.message;
            flashRef.value.error('Error creating new template!');
            console.error(response.data.message);
        });
};

const templateIsDefault = (pTemplate) => {
    return pTemplate.id === props.defaultTemplate.id;
};

const userIsJim = () => {
    return window.currentUser.username === 'chartbook';
};

const close = () => {
    showModal.value = false;
};

watch(() => props.initialDefault, (newInitial) => {
    template.value = newInitial === null ? 0 : newInitial;
});

defineExpose({
    openModal,
    saveNewTemplate,
    reset,
});
</script>

<style lang="scss">
#modalTemplateSave {
    .modal-container {
        width: 24rem;
        @screen xs {
            width: 100%;
        }

        .templates-wrapper {
            @apply m-0 mt-2 mb-2 p-0 text-sm;

            .form-group-addon.form-group-addon-sm {
                margin-bottom: 0 !important;
                .input-addon-field,
                .input-addon-item {
                    box-shadow: none !important;
                }
            }

            .select-template-dropdown {
                @apply pr-6;
            }

            .show-templates-icon {
                position: absolute;
                top: 8px;
                right: 7px;
                cursor: pointer;
            }

            .template-list {
                overflow-y: scroll;
                overflow-x: hidden;
                margin-top: -5px;
                @apply max-h-48;
                @apply mb-3 px-3 py-2 border border-gray-400 border-dotted border-t-0;

                li {
                    @apply text-sm cursor-pointer my-1;
                    &:hover {
                        @apply text-blue-500;
                        i {
                            @apply text-blue-500;
                        }
                    }
                }
            }
        }
    }
}

#modalTemplates {
    .modal-container {
        width: 24rem;
        @screen xs {
            width: 100%;
        }

        .templates-wrapper {
            @apply m-0 mt-2 mb-2 p-0 text-sm;
            .column-title {
                @apply font-bold mb-1;
            }

            .template-list {
                overflow-y: scroll;
                overflow-x: hidden;
                @apply max-h-48;
                @apply px-3 py-2 border border-gray-400 border-dotted;

                li {
                    @apply text-sm cursor-pointer my-1;
                    &:hover {
                        @apply text-blue-500;
                        i {
                            @apply text-blue-500;
                        }
                    }
                }
            }
        }
    }
}
</style>
